import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

// Importing icons to match the image
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import DomainIcon from '@material-ui/icons/Domain';
import TrackChangesIcon from '@material-ui/icons/TrackChanges';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';

export const PRODUCTION_SUMMARY_METRICS = [
    { label: 'Demand', value: '12.4M cases', delta: '+0.8M vs plan', tone: 'blue', icon: <TrendingUpIcon />, deltaTone: 'good' },
    { label: 'Base Production Plan', value: '11.8M cases', delta: '95% demand coverage', tone: 'green', icon: <DomainIcon />, deltaTone: 'good' },
    { label: 'Optimized Plan', value: '12.25M cases', delta: '98.8% coverage', tone: 'purple', icon: <TrackChangesIcon />, deltaTone: 'blue' },
    { label: 'Value Protected', value: '₹38 Cr', delta: 'from re-planning', tone: 'orange', icon: <AttachMoneyIcon />, deltaTone: 'good' },
];

export const PRODUCTION_WEEKLY_REPLAN = [
    { week: 'W39', plan: 480, latest: 500, optimized: 490 },
    { week: 'W40', plan: 540, latest: 610, optimized: 590 },
    { week: 'W41', plan: 590, latest: 700, optimized: 680 },
    { week: 'W42', plan: 620, latest: 705, optimized: 680 },
    { week: 'W43', plan: 640, latest: 680, optimized: 660 },
    { week: 'W44', plan: 660, latest: 690, optimized: 670 },
];

export const PRODUCTION_CHANGE_DRIVERS = [
    { label: 'Demand upside', detail: 'Premium A • North / West', tag: '+180k cases', tone: 'warn' },
    { label: 'Demand downside', detail: 'Core D • East', tag: '−90k cases', tone: 'good' },
    { label: 'Capacity constraint', detail: 'South L2 • W41–W42', tag: '−70k cases', tone: 'warn' },
    { label: 'Packaging constraint', detail: '750ml glass • W40', tag: '+55k at risk', tone: 'warn' },
    { label: 'Mix opportunity', detail: 'Premium F • West', tag: '+₹6 Cr margin', tone: 'good' },
];

export const PRODUCTION_RECOMMENDED_CHANGES = [
    { site: 'North L1', brand: 'Premium A', original: '145k', latest: '190k', optimized: '185k', delta: '+40k', deltaTone: 'warn', reason: 'Demand upside; available flex', impact: '₹8 Cr revenue' },
    { site: 'West L1', brand: 'Premium F', original: '132k', latest: '175k', optimized: '170k', delta: '+38k', deltaTone: 'warn', reason: 'Premium mix / margin opportunity', impact: '₹6 Cr margin' },
    { site: 'South L2', brand: 'Premium C', original: '148k', latest: '178k', optimized: '120k', delta: '−28k', deltaTone: 'bad', reason: 'Line >95%; shift 58k North', impact: 'Service protected' },
    { site: 'East L2', brand: 'Core D', original: '118k', latest: '82k', optimized: '90k', delta: '−28k', deltaTone: 'bad', reason: 'Demand softened; avoid excess', impact: '₹5 Cr inventory' },
    { site: 'Central L1', brand: 'Value E', original: '94k', latest: '110k', optimized: '108k', delta: '+14k', deltaTone: 'warn', reason: 'Fill emerging availability gap', impact: '₹3 Cr revenue' },
];

// Sampled directly from the reference screenshot so the render is pixel-matched.
const CHART_COLORS = {
    plan: '#b4c3dc',
    latest: '#ef6617',
    optimized: '#1c4371',
};

export class Production extends React.Component {
    getChartOptions() {
        const categories = PRODUCTION_WEEKLY_REPLAN.map((w) => w.week);
        const planData = PRODUCTION_WEEKLY_REPLAN.map((w) => w.plan);
        const latestData = PRODUCTION_WEEKLY_REPLAN.map((w) => w.latest);
        const optimizedData = PRODUCTION_WEEKLY_REPLAN.map((w) => w.optimized);

        return {
            chart: {
                type: 'column',
                height: 300,
                backgroundColor: 'transparent',
                style: { fontFamily: 'inherit' },
            },
            title: { text: undefined },
            credits: { enabled: false },
            xAxis: {
                categories,
                lineColor: '#eef0f6',
                tickColor: '#eef0f6',
                labels: { style: { color: '#6b7690', fontSize: '13px' } },
            },
            yAxis: {
                title: { text: undefined },
                gridLineColor: '#f1f3f8',
                labels: { style: { color: '#6b7690', fontSize: '12px' } },
            },
            legend: {
                align: 'center',
                verticalAlign: 'top',
                symbolRadius: 2,
                itemStyle: { color: '#16305c', fontWeight: '600', fontSize: '13px' },
            },
            tooltip: {
                shared: true,
                valueSuffix: 'k cases',
            },
            plotOptions: {
                column: {
                    borderRadius: 4,
                    borderWidth: 0,
                    groupPadding: 0.15,
                    pointPadding: 0.08,
                },
            },
            series: [
                {
                    name: 'Original Plan',
                    data: planData,
                    color: CHART_COLORS.plan,
                },
                {
                    name: 'Latest Demand',
                    data: latestData,
                    color: CHART_COLORS.latest,
                },
                {
                    name: 'Optimized Plan',
                    data: optimizedData,
                    color: CHART_COLORS.optimized,
                },
            ],
        };
    }

    render() {
        return (
            <div className="cc-page">
                <div className="cc-header">
                    <div className="cc-header-text">
                        <h1>Production Optimizer — Plan vs Demand</h1>
                        <p>Continuously re-plan production as demand changes — while protecting service, margin and feasible capacity.</p>
                    </div>
                    <div className="cc-header-filters">
                        <button className="cc-filter-btn">Next 8 Weeks</button>
                        <button className="cc-filter-btn">All Sites</button>
                        <button className="cc-filter-btn">All Brands</button>
                    </div>
                </div>

                {/* Updated Metric Cards to match the image */}
                <div className="cc-metric-row">
                    {PRODUCTION_SUMMARY_METRICS.map((metric) => (
                        <div className={`cc-metric-card tone-${metric.tone}`} key={metric.label}>
                            <div className="cc-metric-icon-wrap">{metric.icon}</div>
                            <div className="cc-metric-content">
                                <div className="cc-metric-label">{metric.label}</div>
                                <div className="cc-metric-value">{metric.value}</div>
                                <div className={`cc-metric-delta cc-delta-${metric.deltaTone}`}>
                                    {metric.delta}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="cc-main-row">
                    <div className="cc-panel cc-chart-panel">
                        <h2>Demand-Driven Production Re-plan</h2>
                        <p className="cc-panel-sub">Compare original plan &#8594; latest demand &#8594; optimized production by week</p>

                        <HighchartsReact highcharts={Highcharts} options={this.getChartOptions()} />
                    </div>

                    <div className="cc-panel cc-signals-panel">
                        <h2>What Changed vs. Original Plan?</h2>
                        <div className="cc-signal-list">
                            {PRODUCTION_CHANGE_DRIVERS.map((signal) => (
                                <div className="dmd-signal-row" key={signal.label}>
                                    <div className="dmd-signal-text-wrap">
                                        <span className="dmd-signal-label">{signal.label}</span>
                                        <span className="dmd-signal-detail">{signal.detail}</span>
                                    </div>
                                    <span className={`dmd-signal-tag dmd-tone-${signal.tone}`}>{signal.tag}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="cc-panel cc-actions-panel">
                    <h2>Recommended Production Changes</h2>
                    <p className="cc-panel-sub">AI recommendations reconcile demand changes with material, line and capacity constraints</p>

                    <table className="cc-actions-table">
                        <thead>
                            <tr>
                                <th>Site / Line</th>
                                <th>Brand / SKU</th>
                                <th>Original</th>
                                <th>Latest Demand</th>
                                <th>Optimized</th>
                                <th>&#916; vs Plan</th>
                                <th>Reason</th>
                                <th>Financial Impact</th>
                            </tr>
                        </thead>
                        <tbody>
                            {PRODUCTION_RECOMMENDED_CHANGES.map((row) => (
                                <tr key={`${row.site}-${row.brand}`}>
                                    <td>{row.site}</td>
                                    <td>{row.brand}</td>
                                    <td>{row.original}</td>
                                    <td>{row.latest}</td>
                                    <td>{row.optimized}</td>
                                    <td className={`cc-delta-${row.deltaTone}`}>{row.delta}</td>
                                    <td>{row.reason}</td>
                                    <td className="cc-delta-good">{row.impact}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <p className="cc-footnote dmd-footnote">
                        Illustrative UX and values. Optimized plan reconciles demand signal against line, material and packaging capacity before committing production changes.
                    </p>
                </div>
            </div>
        );
    }
}

export default Production;